<template>
  <div class="autocomplete">
    <input
      ref="input"
      type="text"
      @focus="emitInput(value)"
      :placeholder="placeholder"
      :value="value"
      @input.stop="onInput"
      @keydown="onKeydown"
      @blur="onBlur"
      class="input autocomplete-input"
    />
    <div class="autocomplete-options" v-if="options.length > 0">
      <div
        class="autocomplete-options-item"
        v-for="(option, optionIdx) in options"
        @mouseenter="idx = optionIdx"
        @click="pick"
        :class="{ 'autocomplete-options-item-active': idx === optionIdx }"
        :key="option[optionKey]"
      >
        <slot :option="option"></slot>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import _ from "lodash";

@Options({
  data() {
    return {
      value: "",
      idx: 0,
    };
  },
  props: {
    placeholder: {
      type: String,
      default: "",
    },
    options: {
      type: Array,
      default: () => [],
    },
    optionKey: {
      type: String,
      default: "_id",
    },
    optionValue: {
      type: String,
      default: "name",
    },
    clearOnPick: {
      type: Boolean,
      default: false,
    },
    fillOnPick: {
      type: Boolean,
      default: true,
    },
    delay: {
      type: Number,
      default: 250,
    },
  },
})
export default class Autocomplete extends Vue {
  value!: string;
  idx!: number;

  placeholder!: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options!: Record<string, any>[];
  optionKey!: string;
  optionValue!: string;
  delay!: number;
  clearOnPick!: boolean;
  fillOnPick!: boolean;

  emitInput!: (value: string) => void;

  created() {
    this.emitInput = _.throttle(this._emitInput, this.delay);
  }

  _emitInput(value: string) {
    this.$emit("input", value);
  }

  pick() {
    const option = this.options[this.idx];
    if (option) {
      this.$emit("pick", option);
      if (this.fillOnPick) {
        this.value = option[this.optionValue];
      } else if (this.clearOnPick) {
        this.value = "";
      }
    }
  }

  onInput(e: Event) {
    const target = e.target as HTMLInputElement;
    this.value = target.value;
    this.emitInput(this.value);
  }

  onBlur() {
    setTimeout(() => this.$emit("clear"), this.delay);
  }

  onKeydown(e: KeyboardEvent) {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (this.idx < this.options.length - 1) {
          this.idx++;
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (this.idx > 0) {
          this.idx--;
        }
        break;
      case "Enter":
        e.preventDefault();
        this.pick();
    }
  }
}
</script>

<style lang="scss">
.autocomplete {
  position: relative;
  box-sizing: border-box;
}

.autocomplete-options {
  position: absolute;
  width: 100%;
  top: 32px;
  border: 1px solid #eee;
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.3);
  box-sizing: border-box;
  z-index: 20;
}

.autocomplete-options-item {
  height: 24px;
  overflow: hidden;
  border-bottom: 1px solid #eee;
  background-color: white;
  cursor: pointer;
}

.autocomplete-options-item-active {
  background-color: #45a;
  color: white;
}
</style>
